import { Anchor, Stack, Table, Tabs, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import { FitTierBadge } from "../../components/matching/FitTierBadge";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../../components/ui";
import { MATCH_TABLE_FIT } from "../../constants/matching";
import { submissionsApi } from "../../lib/api";
import { useQueryFilters } from "../../lib/useQueryFilters";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate } from "../../lib/format";

function SubmissionFitCell({ score, tier }: { score?: number | null; tier?: string | null }) {
  if (score == null) return null;
  if (tier) return <FitTierBadge tier={tier} score={score} />;
  return <Text size="sm">{Math.round(score)}</Text>;
}

const HR_SUBMISSION_TABS = [
  { value: "pending_hr_review", label: "Pending review" },
  { value: "in_progress", label: "In progress" },
  { value: "hired", label: "Hired" },
  { value: "all", label: "All" },
] as const;

const HR_SUBMISSION_TAB_DEFAULT = HR_SUBMISSION_TABS[0].value;
const HR_SUBMISSION_FILTER_DEFAULTS = { tab: HR_SUBMISSION_TAB_DEFAULT };
const HR_SUBMISSION_FILTER_ALLOWED = { tab: HR_SUBMISSION_TABS.map((item) => item.value) };
const HR_SUBMISSION_FILTER_OPTIONS = { allowed: HR_SUBMISSION_FILTER_ALLOWED };

export function HrSubmissionsPage() {
  const submissionFilters = useQueryFilters(HR_SUBMISSION_FILTER_DEFAULTS, HR_SUBMISSION_FILTER_OPTIONS);
  const tab = submissionFilters.values.tab;

  const submissionsQuery = useInfinitePage(["hr-submissions", tab], (page, pageSize) =>
    submissionsApi.list({
      status: tab === "all" ? undefined : tab,
      page,
      page_size: pageSize,
    }),
  );

  const submissions = submissionsQuery.items;
  const isLoading = submissionsQuery.isLoading;

  const { sorted, sortKey, direction, toggle } = useTableSort(submissions, {
    candidate: (r) => r.candidate_name?.toLowerCase() ?? "",
    job: (r) => r.job_title?.toLowerCase() ?? "",
    fit: (r) => r.fit_score,
    status: (r) => r.status,
    stage: (r) => r.current_stage?.name?.toLowerCase() ?? "",
    submitted: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.submissions}
        title={NAV_COPY.common.submissions}
        subtitle="Review candidates moving through your hiring flows."
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && submissionFilters.set({ tab: value })}>
          <Tabs.List>
            {HR_SUBMISSION_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={isLoading}
        error={
          submissionsQuery.isError
            ? submissionsQuery.error instanceof Error
              ? submissionsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void submissionsQuery.refetch()}
        hasMore={submissionsQuery.hasMore}
        loadingMore={submissionsQuery.loadingMore}
        onLoadMore={submissionsQuery.loadMore}
        isEmpty={submissions.length === 0}
        empty={{
          icon: NAV_ICONS.submissions,
          title: "No submissions in this category",
          description: "Try switching tabs to see submissions in other stages.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="candidate" sortKey={sortKey} direction={direction} onSort={toggle}>
                Candidate
              </SortableTh>
              <SortableTh columnKey="job" sortKey={sortKey} direction={direction} onSort={toggle}>
                Job
              </SortableTh>
              <SortableTh columnKey="fit" sortKey={sortKey} direction={direction} onSort={toggle}>
                {MATCH_TABLE_FIT}
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="stage" sortKey={sortKey} direction={direction} onSort={toggle}>
                Current stage
              </SortableTh>
              <SortableTh columnKey="submitted" sortKey={sortKey} direction={direction} onSort={toggle}>
                Submitted
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td>{s.candidate_name ?? "—"}</Table.Td>
                <Table.Td>{s.job_title ?? "—"}</Table.Td>
                <Table.Td>
                  <SubmissionFitCell score={s.fit_score} tier={s.fit_tier} />
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={s.status} />
                </Table.Td>
                <Table.Td>{s.current_stage?.name ?? "—"}</Table.Td>
                <Table.Td>{formatDate(s.created_at)}</Table.Td>
                <Table.Td>
                  <Anchor component={Link} to={`/hr/submissions/${s.id}`} size="sm" data-testid="hr-submission-view">
                    View
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
